import { Box3, Vector3 } from "three";
import type { PipeTraceUpdate } from "./pipe-tracker";

export type PipeMaskPatch = {
  center: Vector3;
  normal: Vector3;
  radius: number;
  thickness: number;
};

export type PipeMaskSnapshot = {
  origin: Vector3;
  patches: readonly PipeMaskPatch[];
  bounds: Box3;
  limited: boolean;
};

const LIMITS = {
  patches: 65_536,
  spacingFactor: 2,
  radiusFraction: 0.25,
  thicknessFraction: 0.1,
  minimumSize: 0.0001,
};

/**
 * @param patch Observed local support disk, relative to the mask origin.
 * @param point Point in the same local frame.
 * @returns Membership near observed surface evidence, not inside a reconstructed solid.
 */
export function belongsToPipePatch(
  patch: PipeMaskPatch,
  point: Vector3,
): boolean {
  const offset = point.clone().sub(patch.center);
  const normalDistance = offset.dot(patch.normal);
  return (
    Math.abs(normalDistance) <= patch.thickness &&
    offset.lengthSq() - normalDistance ** 2 <= patch.radius ** 2
  );
}

/** Owned observed-surface support; snapshots never mutate already-published masks. */
export class PipeSelectionMask {
  private readonly patches = new Map<string, PipeMaskPatch>();
  private readonly bounds = new Box3();
  private limited = false;
  private readonly origin: Vector3;

  /**
   * @param origin Stable world origin for local float precision.
   * @param maximumPatches Optional lower resident patch budget; cannot exceed the built-in cap.
   */
  constructor(
    origin: Vector3,
    private readonly maximumPatches = LIMITS.patches,
  ) {
    this.origin = origin.clone();
  }

  /**
   * @param update Accepted tracking step or fitting samples; gap spans contain no fabricated points.
   * @returns Whether the update fits the resident mask budget.
   */
  add(update: PipeTraceUpdate): boolean {
    const { step } = update;
    if (
      !(step.frontier.radius > 0) ||
      !Number.isFinite(step.frontier.radius) ||
      !(step.tolerance >= 0) ||
      !Number.isFinite(step.tolerance)
    ) {
      throw new Error("Invalid pipe support model");
    }
    const pending = new Map<string, PipeMaskPatch>();
    for (const sample of step.samples) {
      if (
        !sample.point.toArray().every(Number.isFinite) ||
        !sample.normal.toArray().every(Number.isFinite) ||
        !(sample.spacing > 0) ||
        !Number.isFinite(sample.spacing)
      ) {
        continue;
      }
      const supportSpacing = sample.supportSpacing ?? sample.spacing;
      if (!(supportSpacing > 0) || !Number.isFinite(supportSpacing)) continue;
      const radius = Math.max(
        LIMITS.minimumSize,
        Math.min(
          supportSpacing * LIMITS.spacingFactor,
          step.frontier.radius * LIMITS.radiusFraction,
        ),
      );
      const thickness = Math.max(
        LIMITS.minimumSize,
        Math.min(
          step.tolerance + radius ** 2 / (2 * step.frontier.radius),
          step.frontier.radius * LIMITS.thicknessFraction,
        ),
      );
      const center = sample.point.clone().sub(this.origin);
      const normal = sample.normal.clone().normalize();
      const key = center.toArray().join(",");
      const previous = pending.get(key) ?? this.patches.get(key);
      const patch = { center, normal, radius, thickness };
      if (previous) {
        patch.normal.copy(previous.normal);
        patch.radius = Math.max(previous.radius, radius);
        patch.thickness = Math.max(previous.thickness, thickness);
      }
      pending.set(key, patch);
    }
    const added = [...pending.keys()].filter(
      (key) => !this.patches.has(key),
    ).length;
    if (
      this.patches.size + added >
      Math.min(LIMITS.patches, this.maximumPatches)
    ) {
      this.limited = true;
      return false;
    }
    for (const [key, patch] of pending) {
      this.patches.set(key, patch);
      const reach = Math.hypot(patch.radius, patch.thickness);
      this.bounds.expandByPoint(patch.center.clone().addScalar(-reach));
      this.bounds.expandByPoint(patch.center.clone().addScalar(reach));
    }
    return true;
  }

  /** @returns An independent immutable-by-convention mask revision in local coordinates. */
  snapshot(): PipeMaskSnapshot {
    return {
      origin: this.origin.clone(),
      bounds: this.bounds.clone(),
      limited: this.limited,
      patches: [...this.patches.values()].map((patch) => ({
        ...patch,
        center: patch.center.clone(),
        normal: patch.normal.clone(),
      })),
    };
  }
}
